import { formatNumber } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs';
import { Country } from 'src/app/core/resources/country.resource';
import { CountryService } from 'src/app/countries/country.service';
import { ValidityDatas } from '../validity-datas';

@Component({
  selector: 'atm-validity-editor',
  templateUrl: './validity-editor.component.html',
  styleUrls: ['./validity-editor.component.scss'],
})
export class ValidityEditorComponent {
  form: FormGroup;
  displayCountry = this.countries.display;
  filteredCountries: Observable<Country[]>;
  countrySearch = '';

  endDateLocal: Date | null = null;
  startDateLocal: Date | null = null;
  offset = '+00:00';

  get countryControl(): FormControl {
    return this.form.get('country') as FormControl;
  }

  get startDateControl(): FormControl {
    return this.form.get('startDate')?.get('date') as FormControl;
  }

  get startTimeControl(): FormControl {
    return this.form.get('startDate')?.get('time') as FormControl;
  }

  get endDateControl(): FormControl {
    return this.form.get('endDate')?.get('date') as FormControl;
  }

  get endTimeControl(): FormControl {
    return this.form.get('endDate')?.get('time') as FormControl;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: ValidityDatas,
    private fb: FormBuilder,
    private countries: CountryService,
    public dialogRef: MatDialogRef<ValidityEditorComponent>
  ) {
    if (data.timezone?.offset) {
      const add = data.timezone.offset.search(/:/) < 0;
      this.offset = `${data.timezone.offset}${add ? ':00' : ''}`;
    }
    this.form = this.fb.group({
      country: [],
      timezone: this.fb.group({
        value: [data.timezone?.value || 'UTC', Validators.required],
        country: [],
      }),
      startDate: this.fb.group({
        date: [data.start?.date, Validators.required],
        time: [data.start?.time || '00:00', Validators.required],
        inclusive: [data.start?.inclusive || true, Validators.required],
      }),
      endDate: this.fb.group({
        date: [data.end?.date],
        time: [data.end?.time || '00:00'],
        inclusive: [data.end?.inclusive || false],
      }),
    });

    this.updateData();

    this.filteredCountries = this.countryControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((value) =>
        this.countries.list({
          filter: {
            'like.id':
              value && value.length > 0 && value.length <= 2
                ? value
                : undefined,
            'like.name': value && value.length > 2 ? value : undefined,
          },
        })
      )
    );

    this.startTimeControl.valueChanges
      .pipe(debounceTime(1024), distinctUntilChanged())
      .subscribe((time) => {
        this.startTimeControl.patchValue(this.checkTimeValue(time));
        if (time && this.form.value.startDate.date) {
          this.startDateLocal = this.displayLocalTime(
            this.form.value.startDate.date,
            time
          );
        }
      });

    this.startDateControl.valueChanges
      .pipe(debounceTime(512), distinctUntilChanged())
      .subscribe((date) => {
        if (date && this.form.value.startDate.time) {
          this.startDateLocal = this.displayLocalTime(
            date,
            this.form.value.startDate.time
          );
        }
      });

    this.endTimeControl.valueChanges
      .pipe(debounceTime(1024), distinctUntilChanged())
      .subscribe((time) => {
        this.endTimeControl.patchValue(this.checkTimeValue(time));
        if (time && this.form.value.endDate.date) {
          this.endDateLocal = this.displayLocalTime(
            this.form.value.endDate.date,
            time
          );
        }
      });

    this.endDateControl.valueChanges
      .pipe(debounceTime(512), distinctUntilChanged())
      .subscribe((date) => {
        if (date && this.form.value.endDate.time) {
          this.endDateLocal = this.displayLocalTime(
            date,
            this.form.value.endDate.time
          );
        }
      });
  }
  updateData() {
    if (this.form.value.endDate.date && this.form.value.endDate.time) {
      this.endDateLocal = this.displayLocalTime(
        this.form.value.endDate.date,
        this.form.value.endDate.time
      );
    }
    if (this.form.value.startDate.date && this.form.value.startDate.time) {
      this.startDateLocal = this.displayLocalTime(
        this.form.value.startDate.date,
        this.form.value.startDate.time
      );
    }
  }
  setTimezone(value: Country) {
    this.form.patchValue({
      timezone: { value: value.defaultTimezone, country: value },
    });
    this.offset = this.calculateOffset(value.defaultTimezone!);
    this.updateData();
  }

  resetTimeZone() {
    this.offset = '+00:00';
    this.form.patchValue({
      timezone: { value: 'UTC', country: null },
    });

    this.updateData();
  }

  resetInput() {
    this.form.patchValue({
      country: null,
    });
  }

  notSelected(): void {
    this.dialogRef.close();
  }

  onSubmit() {
    const { startDate, endDate, timezone } = this.form.value;

    const validityRange = new ValidityDatas({
      start: startDate,
      end: endDate,
      timezone: {
        value: timezone.value,
        offset: this.offset,
      },
    });
    this.dialogRef.close(validityRange);
  }

  private calculateOffset(timeZone: string): string {
    const d = new Date(2000, 12, 25, 0, 0);
    const dateLocal = d.toLocaleString('en-US', { timeZone });
    const dateUTC = d.toLocaleString('en-US', { timeZone: 'UTC' });

    const offsetNumber =
      (new Date(dateLocal).getTime() - new Date(dateUTC).getTime()) / 3600000;
    let offset = formatNumber(offsetNumber, 'en-US', '2.2');
    offset = `${offsetNumber >= 0 ? '+' : ''}${offset.replace('.', ':')}`;

    return offset;
  }

  private displayLocalTime(date: Date, time: string): Date {
    return new Date(this.setValidityString(date, time));
  }

  private setValidityString(date: Date, time: string): string {
    const day = formatNumber(date.getDate(), 'en-US', '2.');
    const month = formatNumber(date.getMonth() + 1, 'en-US', '2.');
    const year = date.getFullYear();

    return `${year}-${month}-${day}T${time}${this.offset}`;
  }

  private checkTimeValue(time: string): string {
    if (time.length > 2) {
      const first = time[0];
      time = Number(first) > 2 ? '0' + time : time;
      if (time.length === 4 && time.search(/:/) < 0) {
        time = time[0] + time[1] + ':' + time[2] + time[3];
      }
    }
    if (time.length === 5) {
      if (time.search(/:/) < 0) {
        time = time.slice(0, 4);
        time = time[0] + time[1] + ':' + time[2] + time[3];
      }
    }
    return time;
  }
}
